import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config.js';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { isBefore, startOfDay, parseISO, areIntervalsOverlapping } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const CreateAbsenceOnBehalf = ({ currentUser }) => {
    const [eligibleUsers, setEligibleUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [isFetchingUsers, setIsFetchingUsers] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reason, setReason] = useState('');
    const [reasonOther, setReasonOther] = useState('');
    const [notes, setNotes] = useState('');
    const [needsSubCoverage, setNeedsSubCoverage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();

    const reasonOptions = [
        'School-Related',
        'Sickness',
        'Personal Leave',
        'Vacation',
        'Other'
    ];

    // Check if selected user is eligible for substitute coverage
    const isSelectedUserEligibleForSubCoverage = () => {
        if (!selectedUser?.role) return false;
        
        const eligibleRoles = [
            'faculty/staff', 
            'Faculty/Staff', 
            'Teacher', 
            'Educational Administrator', 
            'Educational Admin',
            'System Administrator',
            'Sys Admin'
        ];
        
        return eligibleRoles.some(role => 
            selectedUser.role.toLowerCase().includes(role.toLowerCase()) ||
            role.toLowerCase().includes(selectedUser.role.toLowerCase())
        );
    };

    const fetchEligibleUsers = useCallback(async () => {
        if (!currentUser?.tenantId) {
            setError("Missing tenant information. Please contact your administrator.");
            setIsFetchingUsers(false);
            return;
        }
        
        setIsFetchingUsers(true);
        try {
            const facultyRef = collection(db, 'faculty_staff');
            const q = query(facultyRef, where('tenantId', '==', currentUser.tenantId), where('status', '==', 'active'));
            const querySnapshot = await getDocs(q);
            const allUsers = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            let filteredUsers = [];
            // Admins, Educational Administrators, and HR can submit for anyone in the tenant
            if (currentUser.isSystemAdmin || 
                currentUser.role === 'HR' || 
                currentUser.role === 'Educational Administrator' ||
                currentUser.role === 'Educational Admin' ||
                currentUser.role === 'System Administrator') {
                filteredUsers = allUsers;
            // Supervisors can submit for their direct reports
            } else if (currentUser.isSupervisor) {
                filteredUsers = allUsers.filter(user => user.supervisorId === currentUser.docId);
            }

            // A user cannot submit an absence for themselves on this form
            const finalUsers = filteredUsers.filter(user => user.id !== currentUser.docId).sort((a,b) => a.name.localeCompare(b.name));
            setEligibleUsers(finalUsers);

        } catch (err) {
            console.error("Error fetching eligible users:", err);
            setError(`Could not load the list of employees: ${err.message}`);
        }
        setIsFetchingUsers(false);
    }, [currentUser]);

    useEffect(() => {
        fetchEligibleUsers();
    }, [fetchEligibleUsers]);

    // This function resets the form when a new user is selected
    const handleUserSelection = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        
        // Find and set the selected user object
        const user = eligibleUsers.find(u => u.id === userId);
        setSelectedUser(user);
        
        setStartDate('');
        setEndDate('');
        setReason('');
        setReasonOther('');
        setNotes('');
        setNeedsSubCoverage(false);
        setError('');
        setSuccessMessage('');
    };

    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        // Automatically update end date if it's before the new start date
        if (!endDate || isBefore(parseISO(endDate), parseISO(newStartDate))) {
            setEndDate(newStartDate);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        setSuccessMessage('');

        const selectedEmployee = eligibleUsers.find(u => u.id === selectedUserId);

        // Validation
        if (!selectedEmployee) {
            setError("Please select a valid employee.");
            setIsSubmitting(false); 
            return;
        }
        if (!selectedEmployee.supervisorId) {
            setError("The selected employee is missing a supervisor in their profile. Please update their profile first.");
            setIsSubmitting(false); 
            return;
        }
        if (!startDate || !endDate || !reason) {
            setError("Please fill out all required fields: Start Date, End Date, and Reason.");
            setIsSubmitting(false); 
            return;
        }
        if (isBefore(startOfDay(parseISO(endDate)), startOfDay(parseISO(startDate)))) {
            setError("The end date cannot be before the start date.");
            setIsSubmitting(false); 
            return;
        }
        if (reason === 'Other' && !reasonOther.trim()) {
            setError("Please specify a reason when 'Other' is selected.");
            setIsSubmitting(false); 
            return;
        }

        try {
            // Check for overlapping requests
            const existingRequestsQuery = query(
                collection(db, "absence_requests"),
                where("requesterDocId", "==", selectedEmployee.id),
                where("tenantId", "==", currentUser.tenantId),
                where("status", "in", ["pending", "approved"])
            );

            const querySnapshot = await getDocs(existingRequestsQuery);
            const newRequestInterval = { start: startOfDay(parseISO(startDate)), end: startOfDay(parseISO(endDate)) };
            
            for (const doc of querySnapshot.docs) {
                const existingRequest = doc.data();
                if (existingRequest.startDate && existingRequest.endDate) {
                    const existingRequestInterval = {
                        start: startOfDay(parseISO(existingRequest.startDate)),
                        end: startOfDay(parseISO(existingRequest.endDate))
                    };
                    if (areIntervalsOverlapping(newRequestInterval, existingRequestInterval, { inclusive: true })) {
                        setError("This request overlaps with an existing pending or approved absence for this employee.");
                        setIsSubmitting(false);
                        return;
                    }
                }
            }

            // Check tenant consistency
            if (currentUser.tenantId !== selectedEmployee.tenantId) {
                setError("Tenant mismatch: You and the selected employee belong to different organizations.");
                setIsSubmitting(false);
                return;
            }

            // Prepare the data for the new document
            const absenceRequestData = {
                requesterUid: selectedEmployee.uid || null,
                requesterName: selectedEmployee.name,
                requesterEmail: selectedEmployee.email,
                requesterDocId: selectedEmployee.id,
                tenantId: selectedEmployee.tenantId,
                supervisorId: selectedEmployee.supervisorId,
                startDate: startDate,
                endDate: endDate,
                reason: reason,
                reasonOther: reason === 'Other' ? reasonOther.trim() : '',
                notes: notes.trim(),
                needsSubCoverage: needsSubCoverage,
                status: 'pending',
                createdAt: serverTimestamp(),
                createdBy: currentUser.docId || currentUser.uid,
                createdByName: currentUser.name,
                createdOnBehalf: true,
                history: [{
                    status: 'pending',
                    updatedAt: new Date().toISOString(),
                    updatedBy: `${currentUser.name} (on behalf of ${selectedEmployee.name})`,
                    updatedById: currentUser.docId || currentUser.uid
                }]
            };

            // Submit to Firestore
            await addDoc(collection(db, 'absence_requests'), absenceRequestData);
            
            setSuccessMessage(`Absence request for ${selectedEmployee.name} has been submitted successfully!`);
            
            // If substitute coverage is needed, navigate to RequestSub with pre-filled data
            if (needsSubCoverage) {
                setTimeout(() => {
                    const subRequestData = {
                        prefillData: {
                            dates: startDate === endDate ? [startDate] : [startDate, endDate],
                            reason: reason === 'Other' ? reasonOther : reason,
                            department: selectedEmployee.department,
                            name: selectedEmployee.name,
                            fromAbsenceRequest: true,
                            onBehalfOf: true,
                            onBehalfOfUser: selectedEmployee
                        }
                    };
                    
                    navigate('/request-sub', { state: subRequestData });
                }, 2000);
                
                setSuccessMessage(prev => prev + " Redirecting to substitute request form...");
            } else {
                // Reset the form
                setSelectedUserId('');
                setSelectedUser(null);
                setStartDate('');
                setEndDate('');
                setReason('');
                setReasonOther('');
                setNotes('');
                setNeedsSubCoverage(false);
            }

        } catch (err) {
            console.error("Error submitting absence request:", err);
            
            // Provide specific error messages
            let errorMessage = "Submission failed. Please try again.";
            if (err.code === 'permission-denied') {
                errorMessage = "Permission denied. You may not have the required permissions to create absence requests on behalf of this employee.";
            } else if (err.code === 'invalid-argument') {
                errorMessage = "Invalid data provided. Please check all fields and try again.";
            } else if (err.code === 'unauthenticated') {
                errorMessage = "Authentication error. Please sign out and sign back in.";
            } else {
                errorMessage = `Submission failed: ${err.message}`;
            }
            
            setError(errorMessage);
        }
        setIsSubmitting(false);
    };

    return (
        <div style={{ padding: '20px' }}>
            {/* Header */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '30px',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div>
                    <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                        Create Absence on Behalf
                    </h1>
                    <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
                        Submit an absence request for another employee under your supervision.
                    </p>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{ 
                    backgroundColor: '#f8d7da', 
                    color: '#721c24', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #f5c6cb'
                }}>
                    {error}
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div style={{ 
                    backgroundColor: '#d4edda', 
                    color: '#155724', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    marginBottom: '20px',
                    border: '1px solid #c3e6cb'
                }}>
                    {successMessage}
                </div>
            )}

            {/* Employee Selection */}
            <div style={{ 
                backgroundColor: 'white', 
                border: '1px solid #dee2e6', 
                borderRadius: '8px', 
                padding: '30px',
                marginBottom: '20px'
            }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                    Employee Selection
                </h3>
                
                <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                        Create absence request for *
                    </label>
                    <select 
                        value={selectedUserId} 
                        onChange={handleUserSelection} 
                        style={{ 
                            width: '100%', 
                            padding: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '16px'
                        }}
                    >
                        <option value="">-- Select an Employee --</option>
                        {isFetchingUsers ? (
                            <option disabled>Loading employees...</option>
                        ) : eligibleUsers.length === 0 ? (
                            <option disabled>No eligible employees found</option>
                        ) : (
                            eligibleUsers.map(user => (
                                <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                            ))
                        )}
                    </select>
                    {!isFetchingUsers && eligibleUsers.length === 0 && (
                        <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>
                            {currentUser?.isSystemAdmin || 
                             currentUser?.role === 'HR' || 
                             currentUser?.role === 'Educational Administrator' ||
                             currentUser?.role === 'Educational Admin' ||
                             currentUser?.role === 'System Administrator'
                                ? "No active employees found in your organization."
                                : "You don't have any direct reports, or you're not authorized to create requests on behalf of others."
                            }
                        </p>
                    )}
                </div>
            </div>
            
            {/* Form is only displayed after an employee is selected */}
            {selectedUserId && (
                <div style={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #dee2e6', 
                    borderRadius: '8px', 
                    padding: '30px'
                }}>
                    <form onSubmit={handleSubmit}>
                        {/* Date Selection */}
                        <div style={{ marginBottom: '25px' }}>
                            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                                Absence Dates for {selectedUser?.name}
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                                        Start Date *
                                    </label>
                                    <input 
                                        type="date" 
                                        value={startDate} 
                                        onChange={handleStartDateChange} 
                                        required 
                                        style={{ 
                                            width: '100%', 
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '16px'
                                        }} 
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                                        End Date *
                                    </label>
                                    <input 
                                        type="date" 
                                        value={endDate} 
                                        onChange={(e) => setEndDate(e.target.value)} 
                                        required 
                                        min={startDate} 
                                        style={{ 
                                            width: '100%', 
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '16px'
                                        }} 
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Reason Selection */}
                        <div style={{ marginBottom: '25px' }}>
                            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                                Reason for Absence
                            </h3>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                                    Category *
                                </label>
                                <select 
                                    value={reason} 
                                    onChange={(e) => setReason(e.target.value)} 
                                    required 
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '16px'
                                    }}
                                >
                                    <option value="">-- Select a Reason --</option>
                                    {reasonOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>

                            {reason === 'Other' && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                                        Please Specify Other Reason *
                                    </label>
                                    <input 
                                        type="text" 
                                        value={reasonOther} 
                                        onChange={(e) => setReasonOther(e.target.value)} 
                                        required 
                                        placeholder="Please describe the reason for the absence..."
                                        style={{ 
                                            width: '100%', 
                                            padding: '12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '16px'
                                        }} 
                                    />
                                </div>
                            )}
                        </div>

                        {/* Substitute Coverage Option - Only for eligible users */}
                        {isSelectedUserEligibleForSubCoverage() && (
                            <div style={{ marginBottom: '25px' }}>
                                <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                                    Substitute Coverage
                                </h3>
                                <div style={{ 
                                    padding: '15px', 
                                    backgroundColor: '#f8f9fa', 
                                    border: '1px solid #dee2e6', 
                                    borderRadius: '8px' 
                                }}>
                                    <label style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px', 
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={needsSubCoverage}
                                            onChange={(e) => setNeedsSubCoverage(e.target.checked)}
                                            style={{ transform: 'scale(1.2)' }}
                                        />
                                        {selectedUser?.name} needs substitute teacher coverage for this absence
                                    </label>
                                    <p style={{ 
                                        margin: '10px 0 0 36px', 
                                        fontSize: '14px', 
                                        color: '#6c757d',
                                        lineHeight: '1.4'
                                    }}>
                                        Check this box if {selectedUser?.name} needs a substitute teacher for their classes during this absence. 
                                        After submitting this request, you'll be taken to the substitute request form with 
                                        the absence details pre-filled.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Additional Notes */}
                        <div style={{ marginBottom: '30px' }}>
                            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
                                Additional Information
                            </h3>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                                    Notes (Optional)
                                </label>
                                <textarea 
                                    rows={4} 
                                    value={notes} 
                                    onChange={(e) => setNotes(e.target.value)} 
                                    placeholder="Provide any additional details for the supervisor..."
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '16px',
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>
                        
                        {/* Submit Section */}
                        <div style={{ 
                            borderTop: '1px solid #dee2e6', 
                            paddingTop: '20px',
                            display: 'flex',
                            gap: '15px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                type="button"
                                onClick={() => navigate('/home')}
                                disabled={isSubmitting}
                                style={{
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={isSubmitting || !startDate || !endDate || !reason || (reason === 'Other' && !reasonOther.trim())} 
                                style={{
                                    backgroundColor: isSubmitting ? '#ccc' : '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    padding: '12px 24px',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: (isSubmitting || !startDate || !endDate || !reason || (reason === 'Other' && !reasonOther.trim())) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isSubmitting ? 'Submitting...' : 
                                 needsSubCoverage ? `Submit & Continue to Sub Request for ${selectedUser?.name}` : 
                                 `Submit Request for ${selectedUser?.name}`}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Help Text */}
            <div style={{ 
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
            }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 'bold', color: '#495057' }}>
                    Important Information
                </h4>
                <ul style={{ margin: 0, color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
                    <li>You can only create requests for employees under your supervision</li>
                    <li>The employee's supervisor will be notified and will approve or deny the request</li>
                    <li>Both you and the employee will receive email notifications when the request is processed</li>
                    <li>If substitute coverage is needed, you'll be redirected to complete the substitute request</li>
                </ul>
            </div>
        </div>
    );
};

export default CreateAbsenceOnBehalf;